import { format, parseISO } from 'date-fns';
import type { DossierCalendarEvent, DossierCalendarEventKind } from './dossierCalendar';

import { formatAppDate } from '../../lib/dateFormat';
const TYPE_LABEL: Record<DossierCalendarEventKind, string> = {
  hearing: 'Audience',
  task: 'Tâche',
  prescription: 'Prescription',
  appointment: 'Rendez-vous',
};

export type AgendaHearingFacts = {
  id: string;
  description: string | null;
  court: string | null;
  notes: string | null;
  ownerId: string | null;
};

export type AgendaAttendee = {
  hearingId: string;
  collaboratorId: string | null;
  contactId: string | null;
};

export type AgendaAppointmentFacts = {
  id: string;
  participantIds: string[];
};

export type AgendaTaskFacts = {
  id: string;
  description: string | null;
  assigneeIds: string[];
};

export type AgendaTableSources = {
  hearings: AgendaHearingFacts[];
  attendees: AgendaAttendee[];
  appointments: AgendaAppointmentFacts[];
  tasks: AgendaTaskFacts[];
  collaborators: ReadonlyMap<string, string>;
  contacts: ReadonlyMap<string, string>;
};

export type AgendaTableRow = {
  id: string;
  kind: DossierCalendarEventKind;
  when: string;
  objet: string;
  description: string;
  responsable: string;
  localisation: string;
  participants: string;
  observations: string;
  typeLabel: string;
};

export type AgendaPreviewLine = { label: string; value: string };

function clean(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

function whenLabel(event: DossierCalendarEvent): string {
  const day = event.start.includes('T')
    ? formatAppDate(new Date(event.start))
    : formatAppDate(parseISO(event.start));
  if (event.allDay || !event.start.includes('T')) return day;
  const start = format(new Date(event.start), 'HH:mm');
  if (!event.end?.includes('T')) return `${day} · ${start}`;
  const end = format(new Date(event.end), 'HH:mm');
  return `${day} · ${start}–${end}`;
}

function named(pairs: Array<{ id: string; name: string }>): string {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const pair of pairs) {
    if (!pair.id || seen.has(pair.id)) continue;
    seen.add(pair.id);
    const name = pair.name.trim();
    if (name) names.push(name);
  }
  names.sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  return names.join(', ');
}

function collaboratorNames(ids: string[], names: ReadonlyMap<string, string>): string {
  return named(ids.map((id) => ({ id, name: names.get(id) ?? '' })));
}

export function buildAgendaRows(
  events: DossierCalendarEvent[],
  sources: AgendaTableSources,
): AgendaTableRow[] {
  const hearings = new Map(sources.hearings.map((hearing) => [hearing.id, hearing]));
  const appointments = new Map(sources.appointments.map((item) => [item.id, item]));
  const tasks = new Map(sources.tasks.map((task) => [task.id, task]));
  const people = new Map<string, AgendaAttendee[]>();
  for (const attendee of sources.attendees) {
    const list = people.get(attendee.hearingId) ?? [];
    list.push(attendee);
    people.set(attendee.hearingId, list);
  }

  return events.map((event) => {
    const base = {
      id: event.id,
      kind: event.kind,
      when: whenLabel(event),
      objet: clean(event.title),
      description: '',
      responsable: '',
      localisation: '',
      participants: '',
      observations: '',
      typeLabel: TYPE_LABEL[event.kind],
    };

    if (event.kind === 'hearing' && event.hearingId) {
      const hearing = hearings.get(event.hearingId);
      const attendees = people.get(event.hearingId) ?? [];
      return {
        ...base,
        description: clean(hearing?.description),
        responsable: hearing?.ownerId
          ? clean(sources.collaborators.get(hearing.ownerId))
          : '',
        localisation: clean(hearing?.court),
        observations: clean(hearing?.notes),
        participants: named(attendees.flatMap((person) => {
          if (person.collaboratorId) {
            return [{ id: person.collaboratorId, name: sources.collaborators.get(person.collaboratorId) ?? '' }];
          }
          if (person.contactId) {
            return [{ id: person.contactId, name: sources.contacts.get(person.contactId) ?? '' }];
          }
          return [];
        })),
      };
    }

    if (event.kind === 'appointment' && event.appointmentId) {
      const appointment = appointments.get(event.appointmentId);
      return {
        ...base,
        participants: collaboratorNames(appointment?.participantIds ?? [], sources.collaborators),
      };
    }

    if (event.kind === 'task' && event.taskId) {
      const task = tasks.get(event.taskId);
      return {
        ...base,
        description: clean(task?.description),
        responsable: collaboratorNames(task?.assigneeIds ?? [], sources.collaborators),
      };
    }

    return base;
  });
}

export function agendaPreviewLines(row: AgendaTableRow): AgendaPreviewLine[] {
  const lines: AgendaPreviewLine[] = [];
  if (row.kind === 'hearing') {
    if (row.description) lines.push({ label: 'Descriptif', value: row.description });
    if (row.localisation) lines.push({ label: 'Tribunal', value: row.localisation });
    if (row.observations) lines.push({ label: 'Observations', value: row.observations });
    if (row.responsable) lines.push({ label: 'Responsable', value: row.responsable });
    if (row.participants) lines.push({ label: 'Personnes', value: row.participants });
    return lines;
  }
  if (row.kind === 'appointment') {
    if (row.participants) lines.push({ label: 'Intervenants', value: row.participants });
    return lines;
  }
  if (row.kind === 'task') {
    if (row.description) lines.push({ label: 'Description', value: row.description });
    if (row.responsable) lines.push({ label: 'Responsable', value: row.responsable });
  }
  return lines;
}
